import { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Controller, FormProvider } from 'react-hook-form';
import parseZone from 'helpers/parseZone';
import { useMutation, useQueryClient } from 'react-query';
import NumberFormat from 'react-number-format';
import useFormWithErrorTracking from 'hooks/useFormWithErrorTracking';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  bookingBoatIdAtom,
  bookingCustomPriceAtom,
  isBookingMultiDayAtom,
  tripFinishDayjsAtom,
  tripFinishFormattedAtom,
  tripStartDayjsAtom,
  tripStartFormattedAtom,
  bookingTripTimeAtom,
  isBookingInitializedAtom,
  selectedBookingPackageAtom,
  bookingDurationAtom,
} from 'components/forms/bookingWidget/jotaiStore';
import { useBoatCalendar } from 'components/forms/bookingWidget/hooks';
import { BookingsApi } from 'swagger/apis/bookings-api';
import OpenApiConfiguration from 'api/OpenApiConfiguration';
import { Booking, DomesticV2BookingsIdInquiryPatchRequest } from 'swagger/models';
import { useBookingPricing } from 'hooks/queries';
import useModal from 'components/remodal/v2/useModal';
import LoadingSpinner from 'components/loading/LoadingSpinner';
import { useUserDetails } from 'auth/mutations/userHooks';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import ErrorMessage from 'components/ui/ErrorMessage/ErrorMessage';
import Input from 'components/ui/Input/Input';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import Button from 'components/ui/Button/Button';
import { useCloseModal } from 'components/remodal/v2/hooks';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import { dollarsWithCentsWithSymbol } from '../../helpers/currency';
import { useBreakpoint } from '../BreakpointProvider';
import { mediaMSmMax } from '../../helpers/breakpoints';
import CalendarInput from '../calendar/CalendarInput';
import TimePicker from '../forms/TimePicker/TimePicker';
import PriceBreakdownModal from './PriceBreakdownModal';
import { apiParamsHourNameMapReverse, createDurationOption } from '../forms/bookingWidget/utils';

const bookingsApi = new BookingsApi(OpenApiConfiguration);

const EditInquiryModal = ({ booking }: { booking: Booking }) => {
  const closeModal = useCloseModal();
  const { enqueueSnackbar } = useLazySnackbar();

  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm || breakpoints.md;

  const [tripStart, setTripStart] = useAtom(tripStartDayjsAtom);
  const [tripTime, setTripTime] = useAtom(bookingTripTimeAtom);
  const setBookingDuration = useSetAtom(bookingDurationAtom);
  const [bookingBoatId, setBookingBoatId] = useAtom(bookingBoatIdAtom);
  const setCustomPrice = useSetAtom(bookingCustomPriceAtom);
  const setTripFinish = useSetAtom(tripFinishDayjsAtom);
  const setIsBookingMultiDay = useSetAtom(isBookingMultiDayAtom);
  const tripStartFormatted = useAtomValue(tripStartFormattedAtom);
  const tripFinishFormatted = useAtomValue(tripFinishFormattedAtom);
  const setBookingPack = useSetAtom(selectedBookingPackageAtom);
  const setIsBookingInitialized = useSetAtom(isBookingInitializedAtom);
  const rudderTrack = useRudderStackTrack();

  const queryClient = useQueryClient();
  // flag for price reset when date changes
  const [resetPrice, setResetPrice] = useState(false);
  const diffHours = parseZone(booking?.trip_finish).diff(parseZone(booking?.trip_start), 'hour', true);

  const { userId } = useUserDetails();
  const { bookingPricing: uncastBookingPricing, isLoading: isPricingLoading } = useBookingPricing(booking);
  const bookingPricing = uncastBookingPricing as Booking;
  const { boatCalendar, isLoading: isBoatCalendarLoading } = useBoatCalendar({
    startOn: tripStartFormatted,
    endOn: tripFinishFormatted,
    boatId: bookingBoatId,
    packageType: booking.package_type,
    bookingId: booking.id,
  });

  const { mutate: updateBookingInquiryMutate, isLoading: processing } = useMutation(
    ({ id, body }: { id: string; body: DomesticV2BookingsIdInquiryPatchRequest }) =>
      bookingsApi.domesticV2BookingsIdInquiryPatch(id, body),
    {
      onSuccess() {
        queryClient.invalidateQueries(['conversationDetail', booking.conversation_id]);
        enqueueSnackbar('Your inquiry has been updated.', {
          variant: 'success',
          autoHideDuration: 2000,
        });
        closeModal();
      },
      onError(res) {
        console.error(res);
        closeModal();
      },
    }
  );

  const initialEditPrice =
    booking.special_offer_price === 0
      ? booking.boat_price / 100
      : (booking.special_offer_price + booking.boat_price) / 100;

  const isOwner = booking.owner.id === userId || booking?.boat?.boat_manager?.id === userId;

  const methods = useFormWithErrorTracking({
    mode: 'onChange',
    defaultValues: {
      editPrice: initialEditPrice,
      tripStart: null,
      tripTime: null,
    },
  });
  const { handleSubmit, register, errors, setValue, watch, control } = methods;

  // sync special offer
  useEffect(() => {
    // * 100 because it needs to be converted back to cents (gets divided by 100 up above in initialEditPrice)
    setCustomPrice(watch('editPrice') * 100);
  }, [watch('editPrice')]);

  // sync form with tripStart & tripTime
  useEffect(() => {
    setValue('tripStart', tripStart);
    setValue('tripTime', tripTime);
  }, [tripStart, tripTime]);

  useEffect(() => {
    setTripStart(parseZone(booking.trip_start));
    setTripFinish(parseZone(booking.trip_finish));
    setIsBookingMultiDay(booking.trip_type === 'multi_day');

    const duration_option = createDurationOption(apiParamsHourNameMapReverse[booking.trip_type], booking.trip_type);
    setBookingDuration(duration_option);

    setTripTime({
      label: parseZone(booking?.trip_start).format('hh:mm A'),
      value: parseZone(booking?.trip_start).format('HH:mm'),
    });

    // * 100 because it needs to be converted back to cents (gets divided by 100 up above in initialEditPrice)
    setCustomPrice(initialEditPrice * 100);
    setBookingBoatId(booking.boat.id);
    setIsBookingInitialized(true);
    setBookingPack(booking.package);
  }, [booking]);

  useEffect(() => {
    if (resetPrice) {
      setValue('editPrice', bookingPricing.boat_price / 100);
      setResetPrice(false);
    }
  }, [resetPrice]);

  const approve = async ({ editPrice: editPriceChanged }) => {
    if (
      tripStart.isSame(parseZone(booking?.trip_start), 'date') &&
      tripTime.value === parseZone(booking?.trip_start).format('HH:mm') &&
      ((isOwner ? initialEditPrice === parseInt(editPriceChanged, 10) : true) || (isOwner ? !editPriceChanged : true))
    ) {
      enqueueSnackbar('Please select a different start date and/or time from the original booking details.', {
        variant: 'error',
        autoHideDuration: 2000,
      });
      return;
    }
    rudderTrack('booking_action_owner', {
      booking: booking.id,
      action: 'edit-inquiry',
      location: 'edit-inquiry-modal',
    });
    updateBookingInquiryMutate({
      id: booking.id,
      body: {
        trip_start: tripStartFormatted,
        trip_finish: tripFinishFormatted,
        trip_start_time: tripTime.value,
        special_offer_price: isOwner ? parseInt(editPriceChanged || initialEditPrice, 10) * 100 : undefined,
      },
    });
  };

  const { modal: priceBreakdownModal, openModal: openPriceBreakdownModal } = useModal(
    <PriceBreakdownModal booking={booking} pricing={bookingPricing} />,
    {
      size: 'kindaSmall',
      trackingName: 'price_breakdown_modal',
    }
  );

  const showBreakdown = () => {
    if (isPricingLoading) return;

    openPriceBreakdownModal();
  };

  const getDescription = () => {
    if (booking.owner.id === userId && booking.state === 'pre_approved')
      return 'Editing your inquiry will automatically update the existing details. Please note your inquiry will remain pre-approved.';
    if (booking.owner.id === userId && booking.state === 'initialized')
      return 'Editing your inquiry will automatically update the existing details. Please note that editing your inquiry will not pre-approve it.';
    return 'Editing your inquiry will automatically update your existing details.';
  };

  if (isBoatCalendarLoading) {
    return (
      <Wrapper>
        <DummyWidth />
        <SpinnerWrapper>
          <LoadingSpinner color="#72D4BA" />
        </SpinnerWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Heading>Edit inquiry</Heading>
      <DummyWidth />
      <Description>{getDescription()}</Description>
      <Section>
        <SectionHeading>Existing details</SectionHeading>
        <Items>
          <Item>
            <Title>Start date</Title>
            <Value>{parseZone(booking?.trip_start).format('MMM D, YYYY • h:mm A')}</Value>
          </Item>
          <Item>
            <Title>End date</Title>
            <Value>{parseZone(booking?.trip_finish).format('MMM D, YYYY • h:mm A')}</Value>
          </Item>
          <Item>
            <Title>Duration</Title>
            <Value>{diffHours >= 24 ? `${Math.ceil(diffHours / 24)} days` : `${diffHours} hours`}</Value>
          </Item>
          <Item>
            <Title>Captain</Title>
            <Value>{booking?.package_type === 'bareboat' ? 'No captain' : 'Captained'}</Value>
          </Item>
          <Item>
            <Title>Passengers</Title>
            <Value>
              {booking?.passengers} {booking?.passengers > 1 ? 'people' : 'person'}
            </Value>
          </Item>
        </Items>
      </Section>
      <Section>
        <SectionHeading>Edit details</SectionHeading>
        <FormProvider {...methods}>
          <Form onSubmit={handleSubmit(approve)}>
            <InputWrapper>
              <CalendarInput
                isOwner={isOwner}
                modalVersion
                resetTripTime
                hidePrice
                hideMultiDay
                onChange={() => {
                  setResetPrice(true);
                }}
              />
              {/* @ts-ignore */}
              <input type="hidden" ref={register('tripStart', { required: true })} name="tripStart" />
              <ErrorMessage error={errors.tripStart} message="Start date is required" />
            </InputWrapper>

            <InputWrapper>
              <TimePicker isOwner={isOwner} calendar={boatCalendar} useDuration modalVersion editable />
              {/* @ts-ignore */}
              <input type="hidden" ref={register('tripTime', { required: true })} name="tripTime" />
              <ErrorMessage error={errors.tripTime} message="Start time is required" />
            </InputWrapper>

            {isOwner && (
              <PriceAdjustment>
                <SectionHeading>
                  Edit price
                  <Tooltip
                    className="price_adjustment_tooltip"
                    place="top"
                    interactive
                    trigger="mouseenter"
                    triggerWith={
                      <QuestionWrapper>
                        <QuestionMark />
                      </QuestionWrapper>
                    }
                  >
                    {isOwner
                      ? 'Edit the price of your boat. Note that changing the date will reset any price adjustment made.'
                      : 'Editing the start date may affect the total price of the booking.'}
                  </Tooltip>
                </SectionHeading>
                <Input
                  hasError={!!errors.editPrice}
                  inputMode="numeric"
                  symbol="$"
                  type="tel"
                  name="editPrice"
                  hasValue={!!watch('editPrice')}
                  placeholder="Boat price"
                  inputComponent={(inputComponentProps) => (
                    <Controller
                      rules={{
                        min: Math.ceil(booking.special_offer_min_price / 100),
                        pattern: /^[0-9]*$/,
                      }}
                      render={({ onChange, value }) => (
                        <NumberFormat
                          thousandSeparator
                          {...inputComponentProps}
                          hasValue={!!watch('editPrice')}
                          value={value}
                          placeholder=""
                          onValueChange={(e) => {
                            onChange(e.value);
                          }}
                          isNumericString
                          allowNegative={false}
                        />
                      )}
                      name="editPrice"
                      control={control}
                    />
                  )}
                />
                <ErrorMessage
                  error={!!errors.editPrice && errors.editPrice.type === 'min'}
                  message={`You must price your boat at least ${dollarsWithCentsWithSymbol(
                    Math.ceil(booking.special_offer_min_price / 100) * 100
                  )}.`}
                />
                <ErrorMessage
                  error={!!errors.editPrice && errors.editPrice.type === 'pattern'}
                  message="Please round to the nearest dollar amount."
                />
              </PriceAdjustment>
            )}

            <Items>
              {isOwner && (
                <Item>
                  <Title>Total (excl. fees)</Title>
                  <Value>
                    {isPricingLoading ? (
                      <ElasticDots />
                    ) : (
                      dollarsWithCentsWithSymbol(
                        (bookingPricing?.boat_price || 0) +
                          (bookingPricing?.special_offer_price || 0) +
                          (bookingPricing?.captain_price || 0)
                      )
                    )}
                  </Value>
                </Item>
              )}

              {!isOwner && (
                <Item>
                  <Title>Boat price</Title>
                  <Value>
                    {isPricingLoading ? (
                      <ElasticDots />
                    ) : (
                      dollarsWithCentsWithSymbol(
                        (bookingPricing?.boat_price || 0) + (bookingPricing?.special_offer_price || 0)
                      )
                    )}
                  </Value>
                </Item>
              )}
              {!isOwner && bookingPricing?.captain_price > 0 && (
                <Item>
                  <Title>Captain price</Title>
                  <Value>
                    {isPricingLoading ? <ElasticDots /> : dollarsWithCentsWithSymbol(bookingPricing?.captain_price)}
                  </Value>
                </Item>
              )}
              {isOwner && (
                <Item>
                  <Title>
                    You earn {!smallScreen && isOwner && <BreakDown onClick={showBreakdown}>Price breakdown</BreakDown>}
                  </Title>
                  <GreenValue>
                    {isPricingLoading ? (
                      <ElasticDots />
                    ) : (
                      dollarsWithCentsWithSymbol(bookingPricing?.earnings_including_captain)
                    )}
                  </GreenValue>
                </Item>
              )}
              {smallScreen && isOwner && (
                <MobileBreakdown onClick={showBreakdown}>
                  <span>Price breakdown</span>
                </MobileBreakdown>
              )}
              {!isOwner && (
                <Item>
                  <Title>Booking total (excl. fees)</Title>
                  <Value>
                    {isPricingLoading ? (
                      <ElasticDots />
                    ) : (
                      dollarsWithCentsWithSymbol(
                        (bookingPricing?.boat_price || 0) +
                          (bookingPricing?.special_offer_price || 0) +
                          (bookingPricing?.captain_price || 0)
                      )
                    )}
                  </Value>
                </Item>
              )}
            </Items>

            <ButtonsWrapper>
              <Button disabled={processing} type="submit" text="save edits" variant={isOwner ? 'yellow' : 'renter'} />
              <Button
                disabled={processing}
                text="cancel"
                variant={isOwner ? 'owner' : 'renter'}
                outline
                onClick={closeModal}
              />
            </ButtonsWrapper>
          </Form>
        </FormProvider>
      </Section>
      {priceBreakdownModal}
    </Wrapper>
  );
};

const ElasticDots = () => (
  <LoadingDots className="snippet">
    <div className="stage">
      <div className="dot-pulse" />
    </div>
  </LoadingDots>
);

const dotElasticAfter = keyframes`
  0% {
    box-shadow: 10014px 0 0 -5px #72D4BA;
  }
  30% {
    box-shadow: 10014px 0 0 2px #72D4BA;
  }
  60%,
  100% {
    box-shadow: 10014px 0 0 -5px #72D4BA;
  }
`;

const dotElastic = keyframes`
  0% {
    box-shadow: 9999px 0 0 -5px #72D4BA;
  }
  30% {
    box-shadow: 9999px 0 0 2px #72D4BA;
  }
  60%,
  100% {
    box-shadow: 9999px 0 0 -5px #72D4BA;
  }
`;

const dotElasticBefore = keyframes`
  0% {
    box-shadow: 9984px 0 0 -5px #72D4BA;
  }
  30% {
    box-shadow: 9984px 0 0 2px #72D4BA;
  }
  60%,
  100% {
    box-shadow: 9984px 0 0 -5px #72D4BA;
  }
`;

const LoadingDots = styled.div`
  margin-left: -25px;
  margin-top: 5px;

  .dot-pulse {
    position: relative;
    left: -9999px;
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background-color: #72d4ba;
    color: #72d4ba;
    box-shadow: 9999px 0 0 -5px #72d4ba;
    animation: ${dotElastic} 1.5s infinite linear;
    animation-delay: 0.25s;
  }

  .dot-pulse::before,
  .dot-pulse::after {
    content: '';
    display: inline-block;
    position: absolute;
    top: 0;
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background-color: #72d4ba;
    color: #72d4ba;
  }

  .dot-pulse::before {
    box-shadow: 9984px 0 0 -5px #72d4ba;
    animation: ${dotElasticBefore} 1.5s infinite linear;
    animation-delay: 0s;
  }

  .dot-pulse::after {
    box-shadow: 10014px 0 0 -5px #72d4ba;
    animation: ${dotElasticAfter} 1.5s infinite linear;
    animation-delay: 0.5s;
  }
`;

const SpinnerWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
`;

const InputWrapper = styled.div`
  padding-bottom: 20px;
`;

const DummyWidth = styled.div`
  min-width: 600px;
`;

const PriceAdjustment = styled.div`
  padding-bottom: 25px;
  padding-top: 5px;

  [class*='Input_inputWrapper'] {
    max-width: unset;
    border: 1px solid rgba(142, 150, 151, 0.5);

    [class*='placeholderUp'] {
      font-size: 12px;
    }

    input {
      font-size: 14px;
      padding-top: 19px;
      font-weight: 600;
    }
  }
`;

const QuestionWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 5px;
  margin-top: 3px;
`;

const QuestionMark = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.5961 11.5961C9.05773 14.1345 4.94218 14.1345 2.40379 11.5961C-0.134598 9.05773 -0.134598 4.94218 2.40379 2.40379C4.94218 -0.134598 9.05773 -0.134597 11.5961 2.40379C14.1345 4.94218 14.1345 9.05773 11.5961 11.5961ZM10.8704 10.8704C8.73282 13.008 5.2671 13.008 3.1295 10.8704C0.991911 8.73282 0.991911 5.26709 3.1295 3.1295C5.2671 0.991911 8.73282 0.991911 10.8704 3.1295C13.008 5.2671 13.008 8.73282 10.8704 10.8704Z"
      fill="#0E7873"
    />
    <path
      d="M7.39463 8.4453H6.53205V8.00483C6.53205 7.84577 6.54734 7.70201 6.57793 7.57354C6.60852 7.44507 6.65134 7.32884 6.7064 7.22484C6.76758 7.12084 6.83793 7.02295 6.91746 6.93119C7.0031 6.83331 7.09793 6.73543 7.20193 6.63755L7.54145 6.31637C7.67604 6.20014 7.78616 6.07473 7.8718 5.94014C7.96357 5.79943 8.00945 5.62202 8.00945 5.40791C8.00945 5.11426 7.91463 4.88179 7.72498 4.7105C7.54145 4.53309 7.30287 4.44438 7.00922 4.44438C6.70334 4.44438 6.45864 4.54227 6.27511 4.73803C6.09158 4.93379 5.98452 5.17238 5.95393 5.45379L5.073 5.35285C5.1097 5.08979 5.18617 4.85426 5.30241 4.64626C5.41864 4.43215 5.56241 4.25168 5.7337 4.10486C5.91111 3.95803 6.11299 3.84792 6.33934 3.77451C6.56569 3.69498 6.80734 3.65521 7.06428 3.65521C7.32122 3.65521 7.5598 3.69192 7.78004 3.76533C8.00639 3.83874 8.20521 3.94886 8.37651 4.09568C8.5478 4.23639 8.68239 4.4138 8.78027 4.62791C8.87815 4.83591 8.92709 5.07756 8.92709 5.35285C8.92709 5.54861 8.90568 5.7199 8.86285 5.86673C8.82003 6.01355 8.75886 6.14814 8.67933 6.27049C8.60592 6.38672 8.51415 6.49684 8.40403 6.60084C8.29392 6.70484 8.17462 6.8119 8.04615 6.92201C7.9238 7.02601 7.8198 7.12084 7.73416 7.20648C7.64851 7.28601 7.58122 7.3686 7.53228 7.45425C7.48334 7.53989 7.44663 7.63472 7.42216 7.73871C7.40381 7.84271 7.39463 7.97118 7.39463 8.12412V8.4453ZM7.62404 9.73917C7.62404 9.89823 7.56592 10.0389 7.44969 10.1613C7.33345 10.2836 7.18663 10.3448 7.00922 10.3448C6.83793 10.3448 6.6911 10.2867 6.56875 10.1705C6.45252 10.0481 6.3944 9.90741 6.3944 9.74835C6.3944 9.58317 6.45252 9.44247 6.56875 9.32623C6.68499 9.20388 6.83181 9.14271 7.00922 9.14271C7.18663 9.14271 7.33345 9.20082 7.44969 9.31706C7.56592 9.43329 7.62404 9.574 7.62404 9.73917Z"
      fill="#0E7873"
    />
  </svg>
);

const BreakDown = styled.span`
  color: #0e7873;
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
  padding-left: 10px;
`;

const MobileBreakdown = styled(BreakDown)`
  width: 100%;
  display: flex;

  span {
    margin-left: auto;
  }
`;

const Form = styled.form`
  [class*='ErrorMessage'] {
    margin-bottom: 0;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  padding-top: 25px;
  flex-flow: column;
  gap: 20px;
`;

const Section = styled.div`
  border-top: 1px solid #dbdfe5;
  padding-top: 25px;
  margin-top: 25px;

  textarea {
    resize: none;
    min-height: 105px;
  }
`;

const SectionHeading = styled.div`
  font-weight: bold;
  display: flex;
  padding-bottom: 10px;
  font-size: 16px;
`;

const Items = styled.div``;

const Item = styled.div`
  display: flex;
  padding-bottom: 5px;
`;

const Title = styled.div`
  font-weight: 500;
`;

const Value = styled.div`
  justify-self: flex-end;
  margin-left: auto;
  font-weight: 600;
`;

const GreenValue = styled(Value)`
  color: #2a8500;
  font-weight: 700;
`;

const Wrapper = styled.div`
  ${mediaMSmMax} {
    font-size: 14px;
  }
`;

const Heading = styled.h1`
  font-weight: bold;
  font-size: 24px;
  margin-bottom: 10px;

  ${mediaMSmMax} {
    display: none;
  }
`;

const Description = styled.div`
  max-width: fit-content;
`;

export default EditInquiryModal;
